import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createPurchaseOrderFromPr,
  listPurchaseOrders,
  listPurchaseRequestLines,
  listPurchaseRequests,
} from '../../api/purchasing';
import { listSuppliers } from '../../api/masterData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { PoDetailDrawer } from '../../components/PoDetailDrawer';
import {
  Button,
  ClickableRow,
  EmptyState,
  ErrorNote,
  FieldLabel,
  HintNote,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { PurchaseOrder, PurchaseOrderStatus, PurchaseRequest, PurchaseRequestLine, Supplier } from '../../types/domain';

const PO_STATUSES: PurchaseOrderStatus[] = ['DRAFT', 'ISSUED', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'];

/**
 * Purchase Orders — list + create-from-approved-PR
 * (fn_create_purchase_order_from_pr, migration 0032, permission-gated +
 * branch-scoped, requires the source PR to be APPROVED). Every PO number
 * is clickable — it opens a real read-only detail drawer (supplier,
 * source PR, branch, timestamps, lines with qty/UOM/receiving progress)
 * backed by listPurchaseOrderLines, not a fabricated preview.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function PurchaseOrdersPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [allPrs, setAllPrs] = useState<PurchaseRequest[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedPo, setSelectedPo] = useState<PurchaseOrder | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [supplierFilter, setSupplierFilter] = useState('ALL');

  const canCreate = hasPermission('purchase_order', 'create');
  const approvedPrs = allPrs.filter((pr) => pr.status === 'APPROVED');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [poRows, prRows, supplierRows] = await Promise.all([
        listPurchaseOrders(activeBranchId ?? undefined),
        listPurchaseRequests(activeBranchId ?? undefined),
        listSuppliers(),
      ]);
      setPos(poRows);
      setAllPrs(prRows);
      setSuppliers(supplierRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // PO (and its source-PR picker) is branch-scoped — re-fetch on branch change.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const supplierName = (id: string) => suppliers.find((s) => s.supplier_id === id)?.name ?? id;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pos.filter((po) => {
      if (q && !po.po_number.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && po.status !== statusFilter) return false;
      if (supplierFilter !== 'ALL' && po.supplier_id !== supplierFilter) return false;
      return true;
    });
  }, [pos, search, statusFilter, supplierFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Purchase Orders" subtitle="PO dibuat dari PR yang sudah APPROVED — migration 0007/0032." />
      {canCreate && (
        <Button
          variant="primary"
          onClick={() => setShowForm((v) => !v)}
          style={{ marginBottom: 16 }}
          disabled={approvedPrs.length === 0}
          title={approvedPrs.length === 0 ? 'Tidak ada PR APPROVED yang dapat dikonversi.' : undefined}
        >
          {showForm ? 'Batal' : '+ PO dari PR approved'}
        </Button>
      )}
      {canCreate && approvedPrs.length === 0 && !loading && (
        <HintNote
          text="Tidak ada PR APPROVED yang dapat dikonversi."
          action={
            <a href="/app/purchase-requests" style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Lihat Purchase Requests →
            </a>
          }
        />
      )}
      {showForm && canCreate && (
        <NewPoForm
          approvedPrs={approvedPrs}
          suppliers={suppliers}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(pos, PO_STATUSES);
              return [
                { label: 'Total PO', value: pos.length },
                { label: 'Draft', value: c.DRAFT, tone: 'neutral' as const },
                { label: 'Issued', value: c.ISSUED, tone: 'warning' as const },
                { label: 'Partially Received', value: c.PARTIALLY_RECEIVED, tone: 'info' as const },
                { label: 'Received', value: c.RECEIVED, tone: 'success' as const },
                { label: 'Cancelled', value: c.CANCELLED, tone: 'neutral' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari No. PO..."
            resultCount={total}
            filters={
              <>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 170 }}>
                  <option value="ALL">Semua status</option>
                  <option value="DRAFT">Draft</option>
                  <option value="ISSUED">Issued</option>
                  <option value="PARTIALLY_RECEIVED">Partially Received</option>
                  <option value="RECEIVED">Received</option>
                  <option value="CANCELLED">Cancelled</option>
                </Select>
                {suppliers.length > 0 && (
                  <Select value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)} style={{ width: 180 }}>
                    <option value="ALL">Semua supplier</option>
                    {suppliers.map((s) => (
                      <option key={s.supplier_id} value={s.supplier_id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                )}
              </>
            }
          />
          {pos.length === 0 && <EmptyState text="Belum ada purchase order." />}
          {pos.length > 0 && total === 0 && <EmptyState text="Tidak ada PO yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. PO</th>
                  <th style={thStyle}>Supplier</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Diterbitkan</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((po) => (
                  <ClickableRow key={po.po_id} onClick={() => setSelectedPo(po)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{po.po_number}</td>
                    <td style={tdStyle}>{supplierName(po.supplier_id)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={po.status} />
                    </td>
                    <td style={tdStyle}>{formatDateTime(po.issued_at)}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}

      <PoDetailDrawer po={selectedPo} open={!!selectedPo} onClose={() => setSelectedPo(null)} />
    </div>
  );
}

function NewPoForm({
  approvedPrs,
  suppliers,
  onCreated,
}: {
  approvedPrs: PurchaseRequest[];
  suppliers: Supplier[];
  onCreated: () => void;
}) {
  const [prId, setPrId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [prLines, setPrLines] = useState<PurchaseRequestLine[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [linesLoading, setLinesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!prId) {
      setPrLines([]);
      return;
    }
    setLinesLoading(true);
    listPurchaseRequestLines(prId)
      .then(setPrLines)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLinesLoading(false));
  }, [prId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createPurchaseOrderFromPr({
        prId,
        supplierId,
        lines: prLines.map((l) => ({
          pr_line_id: l.pr_line_id,
          item_id: l.item_id,
          uom_id: l.uom_id,
          qty_ordered: l.qty_requested,
          unit_price: Number(prices[l.pr_line_id] ?? 0),
        })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Purchase Request (APPROVED)">
            <Select value={prId} onChange={(e) => setPrId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {approvedPrs.map((pr) => (
                <option key={pr.pr_id} value={pr.pr_id}>
                  {pr.pr_number}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Supplier">
            <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {suppliers.map((s) => (
                <option key={s.supplier_id} value={s.supplier_id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
      </div>
      {linesLoading && <LoadingNote text="Memuat baris PR..." />}
      {!linesLoading && prLines.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Qty diminta</th>
              <th style={thStyle}>Harga satuan</th>
            </tr>
          </thead>
          <tbody>
            {prLines.map((l) => (
              <tr key={l.pr_line_id}>
                <td style={tdStyle}>{l.qty_requested}</td>
                <td style={tdStyle}>
                  <TextInput
                    type="number"
                    min="0"
                    step="0.01"
                    value={prices[l.pr_line_id] ?? ''}
                    onChange={(e) => setPrices((prev) => ({ ...prev, [l.pr_line_id]: e.target.value }))}
                    required
                    style={{ width: 120 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Button type="submit" variant="primary" loading={submitting} disabled={prLines.length === 0}>
        Buat PO
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
